import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import "./About.css";
import Footer from '../Footer/Footer';
import NavBar from '../NavBar/NavBar';
import ScrollRevealSection from '../Animation/scrollRevealSection';
import { Helmet } from 'react-helmet-async';

const About = () => {
  const [counters, setCounters] = useState({
    kids: 0,
    parents: 0,
    coaches: 0,
    years: 0
  });

  const stats = [
    { id: 'kids', target: 70, label: 'Active Students', icon: 'fa-solid fa-children' },
    { id: 'parents', target: 60, label: 'Happy Parents', icon: 'fa-solid fa-users' },
    { id: 'coaches', target: 4, label: 'Expert Coaches', icon: 'fa-solid fa-person-chalkboard' },
    { id: 'years', target: 11, label: 'Years of Excellence', icon: 'fa-solid fa-calendar-check' }
  ];

  const values = [
    {
      title: "Excellence",
      description: "Striving for the highest standards in gymnastics training and personal development.",
      icon: "fa-solid fa-star"
    },
    {
      title: "Discipline",
      description: "Building character through consistent practice and dedication to the sport.",
      icon: "fa-solid fa-hand-fist"
    },
    {
      title: "Community",
      description: "Creating a supportive environment where every child belongs and thrives.",
      icon: "fa-solid fa-heart"
    },
    {
      title: "Innovation",
      description: "Incorporating modern training techniques and equipment for optimal development.",
      icon: "fa-solid fa-lightbulb"
    }
  ];

  const programs = [
    {
      title: "Gymsters",
      subtitle: "Foundational movement skills",
      description: "Foundational movement skills in a safe & positive environment.",
      ageRange: "Ages 18mo - 5yrs",
      image: "/images/acrobat1.jpg",
      color: "#facc15"
    },
    {
      title: "Recreational",
      subtitle: "Fun & technique combined",
      description: "The perfect mix of fun and world-class tumbling technique.",
      ageRange: "Ages 4 and up",
      image: "/images/acrobat2.jpg",
      color: "#3b82f6"
    },
    {
      title: "Competitive Team",
      subtitle: "Advanced acrobatic gymnastics",
      description: "Competitive and Pre-Competitive Acrobatic Gymnastics teams.",
      ageRange: "Advance level",
      image: "/images/acrobat3.jpg",
      color: "#ef4444"
    }
  ];

  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "instant",   // Change to "smooth" if you prefer
    });
  }, []);

  useEffect(() => {
    const duration = 2000;
    const step = 20;
    const increments = {};

    stats.forEach(stat => {
      increments[stat.id] = stat.target / (duration / step);
    });

    const timer = setInterval(() => {
      setCounters(prev => {
        const newValues = {};
        let allComplete = true;

        stats.forEach(stat => {
          const newValue = prev[stat.id] + increments[stat.id];
          if (newValue >= stat.target) {
            newValues[stat.id] = stat.target;
          } else {
            newValues[stat.id] = Math.ceil(newValue);
            allComplete = false;
          }
        });

        if (allComplete) clearInterval(timer);
        return { ...prev, ...newValues };
      });
    }, step);

    return () => clearInterval(timer);
  }, []);

  return (
    <>
    <Helmet>
     <title>About Us</title>
     <meta
     name="description"
     content="Kigali International Gymnastics Academy, we believe in the power of discipline, creativity, and perseverance. Founded with a passion for acrobatics and gymnastics arts, our academy is dedicated to training and inspiring the next generation of gymnasts and young athletes. 
    "/>
    <link rel="canonical" href="/"/>
    </Helmet> 

    <div className='about-kiga-main'>
      <NavBar />

      {/* Hero Section */}
      <section 
        className='about-hero'
        style={{
          background: `linear-gradient(135deg, rgba(0,0,0,0.75), rgba(0,0,0,0.6)), url('/images/moving2.jpg')`,
          backgroundAttachment: 'fixed',
          backgroundRepeat: 'no-repeat',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className='about-hero-content'>
          <div className='about-hero-text'>
            <span className='hero-subtitle animate-slide-up'>Welcome to</span>
            <h1 className='animate-slide-up-delay'>
              Kigali International <span>Gymnastics Academy</span>
            </h1>
            <div className='hero-breadcrumb animate-slide-up-delay-2'>
              <Link to="/">Home</Link>
              <span>/</span>
              <span className='current'>About Us</span>
            </div>
          </div>
        </div>
        <div className='hero-wave'>
          <svg viewBox="0 0 1200 120" preserveAspectRatio="none">
            <path d="M0,64L80,69.3C160,75,320,85,480,80C640,75,800,53,960,48C1120,43,1280,53,1360,58.7L1440,64L1440,120L1360,120C1280,120,1120,120,960,120C800,120,640,120,480,120C320,120,160,120,80,120L0,120Z" />
          </svg>
        </div>
      </section>

      {/* Who We Are Section */}
      <section className='about-who-we-are'>
        <div className='container'>
          <div className='about-grid'>
            <ScrollRevealSection>
              <div className='about-content'>
                <span className='section-tag'>Who We Are</span>
                <h2>Kigali International <span>Gymnastics Academy</span></h2>
                <p>
                  At Kigali International Gymnastics Academy, we believe in the power of discipline, 
                  creativity, and perseverance. Founded with a passion for acrobatics and gymnastics arts, 
                  our academy is dedicated to training and inspiring the next generation of gymnasts and 
                  young athletes.
                </p>
                <p>
                  Our specialized coaching team brings more than 11 years of experience in coaching and 
                  training young talents. We provide a structured and supportive environment where students 
                  can develop their skills, confidence, and physical strength through Gymnastics and 
                  Acrobatics weekly classes.
                </p>
                <p className='location'>
                  <i className="fa-solid fa-location-dot"></i>
                  Located at Nyarutarama Sports Center (22KG, 13 Avenue, Nyarutarama) - Tennis Club
                </p>
                <Link to="/contact" className='btn-primary'>Contact Us →</Link>
              </div>
            </ScrollRevealSection>

            <ScrollRevealSection>
              <div className='about-stats'>
                <div className='stats-grid'>
                  {stats.map((stat, index) => (
                    <div key={stat.id} className='stat-card' style={{ animationDelay: `${index * 0.1}s` }}>
                      <div className='stat-icon'>
                        <i className={stat.icon}></i>
                      </div>
                      <div className='stat-number'>{counters[stat.id]}+</div>
                      <div className='stat-label'>{stat.label}</div>
                    </div>
                  ))}
                </div>
              </div>
            </ScrollRevealSection>
          </div>
        </div>
      </section>

      {/* Our Values Section */}
      <section className='kiga-about-values'>
        <div className='kiga-about-container'>
          <div className='kiga-about-section-header'>
            <span className='kiga-about-section-tag'>Our Core Values</span>
            <h2>What Drives Us</h2>
            <p>The principles that guide everything we do at KIGA</p>
          </div>
          <div className='kiga-about-values-grid'>
            {values.map((value, index) => (
              <ScrollRevealSection key={index}>
                <div className='kiga-about-value-card'>
                  <div className='kiga-about-value-icon'>
                    <i className={value.icon}></i>
                  </div>
                  <h3>{value.title}</h3>
                  <p>{value.description}</p>
                </div>
              </ScrollRevealSection>
            ))}
          </div>
        </div>
      </section>

      {/* Our Programs Section */}
      <section className='kiga-about-programs'>
        <div className='kiga-about-container'>
          <div className='kiga-about-section-header'>
            <span className='kiga-about-section-tag'>Our Programs</span>
            <h2>Programs We Offer</h2>
            <p>Discover the right path for your child's gymnastics journey</p>
          </div>
          <div className='kiga-about-values-grid'>
            {programs?.map((program, index) => (
              <ScrollRevealSection key={index}>
                <div className='kiga-about-program-card'>
                  <div className='kiga-about-program-image'>
                    <img src={program?.image} alt={program?.title} />
                    <div className='kiga-about-program-overlay' style={{ background: `${program?.color}cc` }}>
                      <div className='kiga-about-program-info'>
                        <h3>{program?.title}</h3>
                        <p>{program?.subtitle}</p>
                      </div>
                    </div>
                  </div>
                  <div className='kiga-about-program-content'>
                    <p className='kiga-about-program-description'>{program.description}</p>
                    <p className='kiga-about-program-age'>{program.ageRange}</p>
                    <Link to="/classes" className='kiga-about-program-link'>Learn More →</Link>
                  </div>
                </div>
              </ScrollRevealSection>
            ))}
          </div>
        </div>
      </section>

      {/* Mission & Vision Section */}
      <section className='kiga-about-mission-vision'>
        <div className='kiga-about-container'>
          <div className='kiga-about-mv-grid'>
            <ScrollRevealSection>
              <div className='kiga-about-mission-card'>
                <div className='kiga-about-mv-icon'>
                  <i className="fa-solid fa-bullseye"></i>
                </div>
                <h3>Our Mission</h3>
                <p>To inspire and develop young athletes through excellence in gymnastics, fostering discipline, confidence, and lifelong skills in a safe and supportive environment.</p>
              </div>
            </ScrollRevealSection>
            <ScrollRevealSection>
              <div className='kiga-about-vision-card'>
                <div className='kiga-about-mv-icon'>
                  <i className="fa-solid fa-eye"></i>
                </div>
                <h3>Our Vision</h3>
                <p>To become East Africa's premier gymnastics academy, recognized for developing world-class athletes and contributing to the growth of gymnastics across the region.</p>
              </div>
            </ScrollRevealSection>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className='about-why-choose'>
        <div className='container'>
          <div className='section-header'>
            <span className='section-tag'>Why Choose Us</span>
            <h2>Why Families Trust KIGA</h2>
          </div>
          <div className='features-grid'>
            <div className='feature-item'>
              <i className="fa-solid fa-certificate"></i>
              <h3>Certified Coaches</h3>
              <p>Experienced and certified gymnastics professionals</p>
            </div>
            <div className='feature-item'>
              <i className="fa-solid fa-shield-heart"></i>
              <h3>Safe Environment</h3>
              <p>State-of-the-art equipment with safety first approach</p>
            </div>
            <div className='feature-item'>
              <i className="fa-solid fa-chart-line"></i>
              <h3>Progressive Curriculum</h3>
              <p>Structured programs for all skill levels</p>
            </div>
            <div className='feature-item'>
              <i className="fa-solid fa-trophy"></i>
              <h3>Competitive Opportunities</h3>
              <p>Regular competitions and showcases</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className='about-cta'>
        <div className='container'>
          <div className='cta-content'>
            <h2>Ready to Start Your Journey?</h2>
            <p>Join KIGA today and discover the joy of gymnastics</p>
            <div className='cta-buttons'>
              <Link to="/classes" className='btn-primary btn-large'>Find Classes</Link>
              <Link to="/contact" className='btn-secondary btn-large'>Contact Us</Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  </>
  );
};

export default About;